import type { LeapRule } from './types.js';

/**
 * Decide whether a given display year is a leap year under the supplied
 * leap rule. Pure function, no state, no allocations on the hot path —
 * called once per `toCanonicalDay` / `toDisplayDate` invocation.
 *
 * `year` is the calendar's display year number (era-relative); the
 * leap-year predicate operates on display years because that is how
 * historical reforms (Gregorian's century-divisibility carve-out) are
 * specified.
 */
export function isLeapYear(rule: LeapRule, year: number): boolean {
  switch (rule.kind) {
    case 'none':
      return false;
    case 'every_n_years':
      // Mathematical modulo so negative years behave predictably.
      return mod(year - rule.phase, rule.every) === 0;
    case 'julian':
      return mod(year, 4) === 0;
    case 'gregorian':
      if (mod(year, 400) === 0) return true;
      if (mod(year, 100) === 0) return false;
      return mod(year, 4) === 0;
  }
}

/** Mathematical modulo: result always has the sign of the divisor. */
export function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}
